import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  ResponsesFiltersState,
  RESPONSES_EXPERIENCE_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  RELOCATION_OPTIONS,
  DRIVER_LICENSE_OPTIONS,
} from '../../hooks/useResponsesFilters';
import { useCurrencies } from '../../hooks/useCurrencies';
import AreaModal from '../ui/AreaModal';
import SearchMultiSelectModal from '../ui/SearchMultiSelectModal';
import { getAreaNameById, compressCityIds, fetchHHLanguages, fetchHHCountries, LanguageOption, CountryOption } from '../../services/hhDictionariesService';
import { fetchAvitoLocations, fetchAvitoNationalities, fetchAvitoResumeSpecializations } from '../../services/avitoDictionariesService';

const AVITO_EDUCATION_OPTIONS = [
  { value: 'higher', label: 'Высшее' },
  { value: 'unfinished-higher', label: 'Неоконченное высшее' },
  { value: 'secondary', label: 'Среднее' },
  { value: 'special-secondary', label: 'Среднее специальное' },
];

const AVITO_SCHEDULE_OPTIONS = [
  { value: 'full-day', label: 'Полный день' },
  { value: 'partial-day', label: 'Неполный день' },
  { value: 'remote', label: 'Удалённо' },
  { value: 'flexible', label: 'Гибкий график' },
  { value: 'shift', label: 'Сменный график' },
  { value: 'fly-in-fly-out', label: 'Вахта' },
];

const AVITO_BUSINESS_TRIP_OPTIONS = [
  { value: 'ready', label: 'Готов' },
  { value: 'sometimes', label: 'Иногда' },
  { value: 'never', label: 'Не готов' },
];

const AVITO_RELOCATION_OPTIONS = [
  { value: 'possible', label: 'Возможен' },
  { value: 'never', label: 'Невозможен' },
];

const AVITO_DRIVER_CATEGORIES = ['a', 'b', 'be', 'c', 'ce', 'd', 'de', 'm', 'tm', 'tb'];

export type ResponsesFiltersInitialSection =
  | 'age'
  | 'experience'
  | 'area'
  | 'salary'
  | 'education'
  | 'driver';

interface ResponsesFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: ResponsesFiltersState;
  onFiltersChange: (updates: Partial<ResponsesFiltersState>) => void;
  onApply: () => void;
  onReset: () => void;
  source?: string;
  initialSection?: ResponsesFiltersInitialSection | null;
}

const ToggleButton: React.FC<{
  value: string;
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  fullWidth?: boolean;
}> = ({ value, selected, onClick, children, fullWidth = false }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2.5 rounded-[10px] border text-sm font-medium transition-all ${
      selected
        ? 'bg-[#E16349] text-white border-[#E16349]'
        : 'bg-white text-[#0A0D14] border-[#E2E4E9] hover:bg-[#F6F8FA]'
    } ${fullWidth ? 'flex-1' : ''}`}
    style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
  >
    {children}
  </button>
);

const MultiToggleButton: React.FC<{
  value: string;
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ value, selected, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2.5 rounded-[10px] border text-sm font-medium transition-all ${
      selected
        ? 'bg-[#E16349] text-white border-[#E16349]'
        : 'bg-white text-[#0A0D14] border-[#E2E4E9] hover:bg-[#F6F8FA]'
    }`}
    style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
  >
    {children}
  </button>
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`relative w-[38px] h-6 rounded-full transition-colors ${
      checked ? 'bg-[#E16349]' : 'bg-[#E2E4E9]'
    }`}
  >
    <div
      className={`absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform ${
        checked ? 'translate-x-[16px]' : 'translate-x-[2px]'
      }`}
    />
  </button>
);

const SelectField: React.FC<{
  label?: string;
  placeholder: string;
  value: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ label, placeholder, value, onClick, disabled }) => (
  <div className="flex flex-col gap-3 w-full">
    {label && (
      <label className="text-[16px] font-medium text-[#0A0D14]">{label}</label>
    )}
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white text-left ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F6F8FA] cursor-pointer'
      }`}
      style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
    >
      <span className={`text-sm ${value ? 'text-[#525866]' : 'text-[#868C98]'}`}>
        {value || placeholder}
      </span>
      <ChevronDownIcon className="w-5 h-5 text-[#525866]" />
    </button>
  </div>
);

const InputField: React.FC<{
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}> = ({ placeholder, value, onChange, type = 'text' }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="flex-1 px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white text-sm text-[#525866] placeholder:text-[#868C98]"
    style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
  />
);


export const ResponsesFiltersModal: React.FC<ResponsesFiltersModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  source = 'hh',
  initialSection = null,
}) => {
  const currencies = useCurrencies();
  const sourceId = (source || 'hh').toString().toLowerCase();
  const isAvitoSource = sourceId === 'avito';
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isCitizenshipModalOpen, setIsCitizenshipModalOpen] = useState(false);
  const [isEducationModalOpen, setIsEducationModalOpen] = useState(false);
  const [isRelocationModalOpen, setIsRelocationModalOpen] = useState(false);
  const [selectedAreaNames, setSelectedAreaNames] = useState<string[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [avitoSpecializations, setAvitoSpecializations] = useState<Array<{ id: string; name: string }>>([]);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const bindSectionRef = (section: ResponsesFiltersInitialSection) => (node: HTMLDivElement | null) => {
    sectionRefs.current[section] = node;
  };

  useEffect(() => {
    if (isOpen && !isAvitoSource) {
      fetchHHLanguages().then(setLanguages);
      fetchHHCountries().then(setCountries);

      const loadAreaNames = async () => {
        if (filters.areaIds.length > 0) {
          const names = await Promise.all(
            filters.areaIds.map(id => getAreaNameById(id).catch(() => id))
          );
          setSelectedAreaNames(names);
        } else {
          setSelectedAreaNames([]);
        }
      };
      loadAreaNames();
    }
  }, [isOpen, isAvitoSource, filters.areaIds]);

  useEffect(() => {
    if (!isOpen || !isAvitoSource) return;
    let cancelled = false;
    Promise.all([
      fetchAvitoNationalities().catch(() => []),
      fetchAvitoResumeSpecializations().catch(() => []),
    ])
      .then(([nationalities, specializations]) => {
        if (!cancelled) {
          setCountries(nationalities as CountryOption[]);
          setAvitoSpecializations(specializations as Array<{ id: string; name: string }>);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isAvitoSource]);

  useEffect(() => {
    if (!isOpen || !isAvitoSource || !initialSection) return;

    const timer = window.setTimeout(() => {
      const sectionNode = sectionRefs.current[initialSection];
      if (typeof sectionNode?.scrollIntoView === 'function') {
        sectionNode.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }

      if (initialSection === 'area') {
        setIsAreaModalOpen(true);
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isOpen, isAvitoSource, initialSection]);

  const handleExperienceToggle = (value: string) => {
    const current = Array.isArray(filters.experience) ? filters.experience : [];
    if (current.includes(value)) {
      onFiltersChange({ experience: current.filter(v => v !== value) });
    } else {
      onFiltersChange({ experience: [...current, value] });
    }
  };

  const handleDriverLicenseToggle = (value: string) => {
    const current = Array.isArray(filters.driverLicenseTypes) ? filters.driverLicenseTypes : [];
    if (current.includes(value)) {
      onFiltersChange({ driverLicenseTypes: current.filter(v => v !== value) });
    } else {
      onFiltersChange({ driverLicenseTypes: [...current, value] });
    }
  };

  const handleAvitoArrayToggle = (field: keyof ResponsesFiltersState, value: string) => {
    const current = Array.isArray(filters[field]) ? (filters[field] as string[]) : [];
    onFiltersChange({
      [field]: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    } as Partial<ResponsesFiltersState>);
  };

  const handleAreaApply = async (areas: string[]) => {
    if (isAvitoSource) {
      onFiltersChange({ areaIds: areas });
      setSelectedAreaNames(areas);
      setIsAreaModalOpen(false);
      return;
    }
    const compressed = await compressCityIds(areas);
    const names = await Promise.all(
      compressed.map(id => getAreaNameById(id).catch(() => id))
    );
    setSelectedAreaNames(names);
    onFiltersChange({ areaIds: compressed });
    setIsAreaModalOpen(false);
  };

  const getSelectedLanguageNames = () => {
    const langs = Array.isArray(filters.language) ? filters.language : [];
    return langs
      .map(id => languages.find(l => l.id === id)?.name || id)
      .join(', ');
  };

  const getSelectedCitizenshipNames = () => {
    const citizenships = Array.isArray(filters.citizenship) ? filters.citizenship : [];
    return citizenships
      .map(id => countries.find(c => c.id === id)?.name || id)
      .join(', ');
  };

  const getSelectedEducationNames = () => {
    const levels = Array.isArray(filters.educationLevel) ? filters.educationLevel : [];
    return levels
      .map(id => EDUCATION_LEVEL_OPTIONS.find(e => e.value === id)?.label || id)
      .join(', ');
  };

  const getSelectedAvitoEducationNames = () => {
    const levels = Array.isArray(filters.avitoEducationLevel) ? filters.avitoEducationLevel : [];
    return levels
      .map(id => AVITO_EDUCATION_OPTIONS.find(e => e.value === id)?.label || id)
      .join(', ');
  };

  const renderAvitoMultiToggle = (
    field: keyof ResponsesFiltersState,
    options: Array<{ value: string; label: string }>
  ) => {
    const selected = Array.isArray(filters[field]) ? (filters[field] as string[]) : [];
    return (
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <MultiToggleButton
            key={option.value}
            value={option.value}
            selected={selected.includes(option.value)}
            onClick={() => handleAvitoArrayToggle(field, option.value)}
          >
            {option.label}
          </MultiToggleButton>
        ))}
      </div>
    );
  };

  const getSelectedRelocationName = () => {
    return RELOCATION_OPTIONS.find(r => r.value === filters.relocation)?.label || '';
  };

  if (!isOpen) return null;

  const renderAvitoFilters = () => (
    <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
      <div className="flex flex-col gap-4">
        <label className="text-[16px] font-medium text-[#0A0D14]">Должность</label>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#525866]">Желаемая</span>
          <InputField
            placeholder="Например, курьер"
            value={filters.desiredPosition || ''}
            onChange={(v) => onFiltersChange({ desiredPosition: v })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-[#525866]">Текущая</span>
          <InputField
            placeholder="Например, водитель"
            value={filters.currentPosition || ''}
            onChange={(v) => onFiltersChange({ currentPosition: v })}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Просмотренность</label>
        <div className="flex gap-3">
          <ToggleButton
            value=""
            selected={!filters.avitoApplicationViewed}
            onClick={() => onFiltersChange({ avitoApplicationViewed: '' })}
            fullWidth
          >
            Все
          </ToggleButton>
          <ToggleButton
            value="false"
            selected={filters.avitoApplicationViewed === 'false'}
            onClick={() => onFiltersChange({ avitoApplicationViewed: filters.avitoApplicationViewed === 'false' ? '' : 'false' })}
            fullWidth
          >
            Новые
          </ToggleButton>
          <ToggleButton
            value="true"
            selected={filters.avitoApplicationViewed === 'true'}
            onClick={() => onFiltersChange({ avitoApplicationViewed: filters.avitoApplicationViewed === 'true' ? '' : 'true' })}
            fullWidth
          >
            Просмотренные
          </ToggleButton>
        </div>
      </div>

      <div ref={bindSectionRef('age')} className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Возраст</label>
        <div className="flex gap-2">
          <InputField placeholder="от" value={filters.ageFrom} onChange={(v) => onFiltersChange({ ageFrom: v })} type="number" />
          <InputField placeholder="до" value={filters.ageTo} onChange={(v) => onFiltersChange({ ageTo: v })} type="number" />
        </div>
      </div>

      <div ref={bindSectionRef('experience')} className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Опыт, лет</label>
        <div className="flex gap-2">
          <InputField placeholder="от" value={filters.avitoExperienceMin} onChange={(v) => onFiltersChange({ avitoExperienceMin: v })} type="number" />
          <InputField placeholder="до" value={filters.avitoExperienceMax} onChange={(v) => onFiltersChange({ avitoExperienceMax: v })} type="number" />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Пол</label>
        <div className="flex gap-3">
          <ToggleButton value="male" selected={filters.gender === 'male'} onClick={() => onFiltersChange({ gender: filters.gender === 'male' ? '' : 'male' })} fullWidth>
            Мужской
          </ToggleButton>
          <ToggleButton value="female" selected={filters.gender === 'female'} onClick={() => onFiltersChange({ gender: filters.gender === 'female' ? '' : 'female' })} fullWidth>
            Женский
          </ToggleButton>
        </div>
      </div>

      <div ref={bindSectionRef('area')}>
        <SelectField
          label="География"
          placeholder="Укажите одну или несколько"
          value={filters.areaIds.length > 0 ? `Выбрано: ${filters.areaIds.length}` : ''}
          onClick={() => setIsAreaModalOpen(true)}
        />
      </div>

      <SelectField
        label="Специализация"
        placeholder="Выберите из списка"
        value={(filters.avitoSpecialization || []).map(id => avitoSpecializations.find(s => s.id === id)?.name || id).join(', ')}
        onClick={() => setIsEducationModalOpen(true)}
      />

      <SelectField
        label="Гражданство"
        placeholder="Укажите гражданство"
        value={getSelectedCitizenshipNames()}
        onClick={() => setIsCitizenshipModalOpen(true)}
      />

      <div ref={bindSectionRef('education')} className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Образование</label>
        {renderAvitoMultiToggle('avitoEducationLevel', AVITO_EDUCATION_OPTIONS)}
        {getSelectedAvitoEducationNames() && <span className="text-xs text-[#525866]">{getSelectedAvitoEducationNames()}</span>}
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">График работы</label>
        {renderAvitoMultiToggle('avitoSchedule', AVITO_SCHEDULE_OPTIONS)}
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Готовность к командировкам</label>
        {renderAvitoMultiToggle('avitoBusinessTrip', AVITO_BUSINESS_TRIP_OPTIONS)}
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Готовность к переезду</label>
        {renderAvitoMultiToggle('avitoRelocation', AVITO_RELOCATION_OPTIONS)}
      </div>

      <div ref={bindSectionRef('salary')} className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Зарплатные ожидания</label>
        <div className="flex gap-2">
          <InputField placeholder="от" value={filters.salaryFrom} onChange={(v) => onFiltersChange({ salaryFrom: v })} type="number" />
          <InputField placeholder="до" value={filters.salaryTo} onChange={(v) => onFiltersChange({ salaryTo: v })} type="number" />
        </div>
      </div>

      <div ref={bindSectionRef('driver')} className="flex flex-col gap-3">
        <label className="text-[16px] font-medium text-[#0A0D14]">Водительское удостоверение</label>
        <div className="flex gap-3">
          <ToggleButton value="yes" selected={filters.avitoDriverLicence === 'yes'} onClick={() => onFiltersChange({ avitoDriverLicence: filters.avitoDriverLicence === 'yes' ? '' : 'yes' })} fullWidth>
            Есть
          </ToggleButton>
          <ToggleButton value="no" selected={filters.avitoDriverLicence === 'no'} onClick={() => onFiltersChange({ avitoDriverLicence: filters.avitoDriverLicence === 'no' ? '' : 'no' })} fullWidth>
            Нет
          </ToggleButton>
        </div>
        <div className="flex flex-wrap gap-2">
          {AVITO_DRIVER_CATEGORIES.map((category) => (
            <MultiToggleButton
              key={category}
              value={category}
              selected={filters.avitoDriverLicenceCategory.includes(category)}
              onClick={() => handleAvitoArrayToggle('avitoDriverLicenceCategory', category)}
            >
              {category.toUpperCase()}
            </MultiToggleButton>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[9999] flex justify-end p-3">
        <div className="bg-[#F5F6F1] w-full max-w-[498px] h-full shadow-2xl overflow-hidden rounded-[24px] flex flex-col">
          <div className="flex items-center justify-between px-6 py-6">
            <h2 className="text-2xl font-medium text-black">Фильтры</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-[#E2E4E9] rounded-lg transition-colors"
            >
              <XMarkIcon className="w-8 h-8 text-[#525866]" />
            </button>
          </div>

          {isAvitoSource ? renderAvitoFilters() : (
          <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-6">
            <div className="flex flex-col gap-4">
              <label className="text-[16px] font-medium text-[#0A0D14]">Должность</label>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-[#525866]">Желаемая</span>
                <InputField
                  placeholder="Например, аналитик"
                  value={filters.desiredPosition || ''}
                  onChange={(v) => onFiltersChange({ desiredPosition: v })}
                />
                <span className="text-xs text-[#525866]">Ищется в названии резюме кандидата</span>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-[#525866]">Текущая</span>
                <InputField
                  placeholder="Например, ведущий аналитик"
                  value={filters.currentPosition || ''}
                  onChange={(v) => onFiltersChange({ currentPosition: v })}
                />
                <span className="text-xs text-[#525866]">Ищется на последнем месте работы</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-[16px] font-medium text-[#0A0D14]">Пол</label>
              <div className="flex gap-3">
                <ToggleButton
                  value="male"
                  selected={filters.gender === 'male'}
                  onClick={() => onFiltersChange({ gender: filters.gender === 'male' ? '' : 'male' })}
                  fullWidth
                >
                  Мужской
                </ToggleButton>
                <ToggleButton
                  value="female"
                  selected={filters.gender === 'female'}
                  onClick={() => onFiltersChange({ gender: filters.gender === 'female' ? '' : 'female' })}
                  fullWidth
                >
                  Женский
                </ToggleButton>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-[16px] font-medium text-[#0A0D14]">Возраст</label>
              <div className="flex gap-2">
                <InputField
                  placeholder="от"
                  value={filters.ageFrom}
                  onChange={(v) => onFiltersChange({ ageFrom: v })}
                  type="number"
                />
                <InputField
                  placeholder="до"
                  value={filters.ageTo}
                  onChange={(v) => onFiltersChange({ ageTo: v })}
                  type="number"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.includeWithoutAge}
                  onChange={(e) => onFiltersChange({ includeWithoutAge: e.target.checked })}
                  className="w-5 h-5 rounded border-2 border-[#CDD0D5] accent-[#E16349]"
                />
                <span className="text-xs text-black">Включая без возраста</span>
              </label>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-[16px] font-medium text-[#0A0D14]">Опыт</label>
              <div className="flex flex-wrap gap-3">
                {RESPONSES_EXPERIENCE_OPTIONS.map((opt) => (
                  <MultiToggleButton
                    key={opt.value}
                    value={opt.value}
                    selected={Array.isArray(filters.experience) && filters.experience.includes(opt.value)}
                    onClick={() => handleExperienceToggle(opt.value)}
                  >
                    {opt.label}
                  </MultiToggleButton>
                ))}
              </div>
            </div>

            <SelectField
              label="Образование"
              placeholder="Образование"
              value={getSelectedEducationNames()}
              onClick={() => setIsEducationModalOpen(true)}
            />

            <SelectField
              label="Языки"
              placeholder="Выберите из списка"
              value={getSelectedLanguageNames()}
              onClick={() => setIsLanguageModalOpen(true)}
            />

            <div className="flex flex-col gap-3">
              <label className="text-[16px] font-medium text-[#0A0D14]">Регион</label>
              <button
                onClick={() => setIsAreaModalOpen(true)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white text-left hover:bg-[#F6F8FA]"
                style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
              >
                <span className={`text-sm ${selectedAreaNames.length > 0 ? 'text-[#525866]' : 'text-[#868C98]'}`}>
                  {selectedAreaNames.length > 0 ? selectedAreaNames.join(', ') : 'Укажите одну или несколько'}
                </span>
                <ChevronDownIcon className="w-5 h-5 text-[#525866]" />
              </button>
              <button
                onClick={() => setIsRelocationModalOpen(true)}
                disabled={filters.areaIds.length === 0}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white text-left ${
                  filters.areaIds.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F6F8FA]'
                }`}
                style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24)' }}
              >
                <span className={`text-sm ${filters.relocation ? 'text-[#525866]' : 'text-[#868C98]'}`}>
                  {getSelectedRelocationName() || 'Живут в регионе или готовы переехать сюда'}
                </span>
                <ChevronDownIcon className="w-5 h-5 text-[#525866]" />
              </button>
            </div>

            <SelectField
              label="Гражданство"
              placeholder="Укажите гражданство"
              value={getSelectedCitizenshipNames()}
              onClick={() => setIsCitizenshipModalOpen(true)}
            />

            <div className="flex flex-col gap-3">
              <label className="text-[16px] font-medium text-[#0A0D14]">Водительское удостоверение</label>
              <div className="flex flex-wrap gap-2">
                {DRIVER_LICENSE_OPTIONS.map((opt) => (
                  <MultiToggleButton
                    key={opt.value}
                    value={opt.value}
                    selected={Array.isArray(filters.driverLicenseTypes) && filters.driverLicenseTypes.includes(opt.value)}
                    onClick={() => handleDriverLicenseToggle(opt.value)}
                  >
                    {opt.label}
                  </MultiToggleButton>
                ))}
              </div>
              <div className="flex items-center justify-between px-3 py-2.5 rounded-[10px] border border-[#E2E4E9] bg-white">
                <span className="text-sm text-black">Есть личный автомобиль</span>
                <Toggle
                  checked={filters.hasVehicle}
                  onChange={(checked) => onFiltersChange({ hasVehicle: checked })}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <label className="text-[16px] font-medium text-[#0A0D14]">Зарплатные ожидания</label>
                <button
                  onClick={() => {
                    const currentIndex = currencies.findIndex(c => c.value === filters.salaryCurrency);
                    const nextIndex = (currentIndex + 1) % currencies.length;
                    onFiltersChange({ salaryCurrency: currencies[nextIndex].value });
                  }}
                  className="flex items-center gap-1 text-[#E16349] text-base"
                >
                  <span>{currencies.find(c => c.value === filters.salaryCurrency)?.label || '₽'}</span>
                  <ChevronDownIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex gap-2">
                <InputField
                  placeholder="от"
                  value={filters.salaryFrom}
                  onChange={(v) => onFiltersChange({ salaryFrom: v })}
                  type="number"
                />
                <InputField
                  placeholder="до"
                  value={filters.salaryTo}
                  onChange={(v) => onFiltersChange({ salaryTo: v })}
                  type="number"
                />
              </div>
            </div>
          </div>
          )}

          <div className="flex gap-4 px-6 pb-6">
            <button
              onClick={onReset}
              className="flex-1 py-3 bg-[#E2E4E9] text-[#525866] rounded-[12px] font-medium text-xl hover:bg-[#CDD0D5] transition-colors"
            >
              Сбросить все
            </button>
            <button
              onClick={() => {
                onApply();
                onClose();
              }}
              className="flex-1 py-3 bg-[#E16349] text-white rounded-[12px] font-medium text-xl hover:bg-[#C9573F] transition-colors"
            >
              Применить
            </button>
          </div>
        </div>
      </div>

      <AreaModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        selectedAreas={filters.areaIds}
        onApply={handleAreaApply}
        fetchData={isAvitoSource ? fetchAvitoLocations : undefined}
        hideCountryTabs={isAvitoSource}
      />

      <SearchMultiSelectModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        title="Языки"
        items={languages}
        selectedItems={filters.language}
        onSelect={(items) => onFiltersChange({ language: items })}
        searchPlaceholder="Поиск языка..."
      />

      <SearchMultiSelectModal
        isOpen={isCitizenshipModalOpen}
        onClose={() => setIsCitizenshipModalOpen(false)}
        title="Гражданство"
        items={countries}
        selectedItems={filters.citizenship}
        onSelect={(items) => onFiltersChange({ citizenship: items })}
        searchPlaceholder="Поиск страны..."
      />

      <SearchMultiSelectModal
        isOpen={isEducationModalOpen}
        onClose={() => setIsEducationModalOpen(false)}
        title={isAvitoSource ? 'Специализация' : 'Образование'}
        items={isAvitoSource ? avitoSpecializations : EDUCATION_LEVEL_OPTIONS.map(o => ({ id: o.value, name: o.label }))}
        selectedItems={isAvitoSource ? filters.avitoSpecialization : filters.educationLevel}
        onSelect={(items) => onFiltersChange(isAvitoSource ? { avitoSpecialization: items } : { educationLevel: items })}
        searchPlaceholder="Поиск..."
        multiSelect={true}
      />

      {isRelocationModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-[10001]"
            onClick={() => setIsRelocationModalOpen(false)}
          />
          <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4">
            <div className="bg-white rounded-[24px] w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-6 border-b border-[#E2E4E9]">
                <h3 className="text-xl font-medium text-[#0A0D14]">Готовность к переезду</h3>
                <button onClick={() => setIsRelocationModalOpen(false)} className="p-1 hover:bg-[#F6F8FA] rounded-lg">
                  <XMarkIcon className="w-6 h-6 text-[#525866]" />
                </button>
              </div>
              <div className="p-4 space-y-2">
                {RELOCATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onFiltersChange({ relocation: opt.value });
                      setIsRelocationModalOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-[10px] transition-colors ${
                      filters.relocation === opt.value
                        ? 'bg-[#FEF3F2] text-[#E16349]'
                        : 'hover:bg-[#F6F8FA] text-[#525866]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ResponsesFiltersModal;
